import React, { useCallback, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import Stack from '@mui/material/Stack';
import { useStringsContext } from '../../../../../contexts/strings';
import BaseDialog, { BaseDialogProps } from '../../../../common/base-dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import firebaseApp from '../../../../../firebase-app';
import { setDoc, collection, doc } from 'firebase/firestore';
import { getPollPath } from '../../../../../utils/paths';
import { FS_PATH_POLLS } from '../../../../../constants';
import { Poll, TextPoll } from '../../types';
import { getStatus } from '../../data';
import PollResultsCard from '../cards/poll-results-card';
import PollNameTimeCard from '../cards/poll-name-time-card';
import PollTextDetailsCard from '../cards/poll-text-details-card';
import PollImagesCard from '../cards/poll-images-card';
import PollTitle from '../poll-title';
import PollValidationPopover from '../poll-validation-popover';
import { useFormValidate } from '../../../../../hooks/formValidate';

interface Props extends BaseDialogProps {
  onEditDone: () => void;
  onCreateDone: (createdPoll: Poll) => void;
  onDelete: () => void;
  selectedPoll: TextPoll | null;
  selectedAbbrv: string;
}

const PollFreeModal = ({
  open,
  onClose,
  onEditDone,
  onCreateDone,
  onDelete,
  selectedPoll,
  selectedAbbrv,
}: Props) => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();

  const anchorEl = useRef<any>(null);
  const [showPopover, setShowPopover] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hideValidationError, setHideValidationError] = useState<boolean>(true);

  const isEditing = selectedPoll !== null;
  const pollStatus = isEditing ? getStatus(selectedPoll) : null;

  const pollId = useMemo(() => {
    return selectedPoll ? selectedPoll.id : doc(collection(firebaseApp.db, FS_PATH_POLLS)).id;
  }, [selectedPoll]);

  const { addFormValue, addFormValidation, isValid, isDirty, editedValues } = useFormValidate();

  const onSubmit = useCallback(async () => {
    if (!isDirty || !isValid) {
      setHideValidationError(false);
      setShowPopover(true);
      return;
    }

    setIsSubmitting(true);

    const updatedPoll = isEditing
      ? { ...selectedPoll }
      : ({
          id: pollId,
          abbrv: selectedAbbrv,
          type: 'free',
          createdAt: moment().toISOString(),
        } as TextPoll);

    Object.entries(editedValues).forEach(([key, value]) => {
      (updatedPoll as any)[key] = value; // Annoying typescript error fix
    });

    try {
      const pollPath = getPollPath(pollId);
      await setDoc(doc(firebaseApp.db, pollPath), updatedPoll);

      enqueueSnackbar(isEditing ? strings.polls.editSuccess : strings.messages.saveSuccess, {
        variant: 'success',
      });
      isEditing ? onEditDone() : onCreateDone(updatedPoll);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.messages.error, {
        variant: 'error',
      });
    }
  }, [
    editedValues,
    enqueueSnackbar,
    isDirty,
    isEditing,
    isValid,
    onCreateDone,
    onEditDone,
    pollId,
    selectedAbbrv,
    selectedPoll,
    strings,
  ]);

  const handleValue = (key: string, value: any) => {
    addFormValue(key, value);
  };

  const handleValidation = (key: string, isValid: boolean) => {
    addFormValidation(key, isValid);
  };

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        title={
          pollStatus !== 'FINISHED'
            ? isEditing
              ? strings.polls.editPoll
              : strings.polls.newTextPoll
            : undefined
        }
      >
        <DialogContent dividers ref={anchorEl}>
          <Stack spacing={4}>
            {isEditing && selectedPoll && (
              <PollTitle selectedPoll={selectedPoll} pollStatus={pollStatus} />
            )}
            {isEditing && selectedPoll && pollStatus !== 'UPCOMING' && (
              <PollResultsCard selectedPoll={selectedPoll} />
            )}
            {pollStatus !== 'FINISHED' && (
              <>
                <PollNameTimeCard
                  title={strings.polls.pollNamePlayerTitle}
                  label={strings.polls.pollNamePlayerLabel}
                  helperText={strings.polls.pollNameTextHelperText}
                  titleStartEnd={strings.polls.pollPlayerTimeStartTitle}
                  selectedPoll={selectedPoll}
                  isEditing={isEditing}
                  onValue={handleValue}
                  onValidation={handleValidation}
                  hideValidationError={hideValidationError}
                />
                <PollTextDetailsCard
                  isEditing={isEditing}
                  selectedPoll={selectedPoll}
                  selectedAbbrv={selectedAbbrv}
                  onValue={handleValue}
                  onValidation={handleValidation}
                  hideValidationError={hideValidationError}
                />
                <PollImagesCard
                  titleBackgroundImage={strings.polls.backgroundImageTitle}
                  pollId={pollId}
                  selectedPoll={selectedPoll}
                  onValue={handleValue}
                  onValidation={handleValidation}
                  hideValidationError={hideValidationError}
                />
                {isEditing && (
                  <Button
                    startIcon={<DeleteIcon />}
                    variant="outlined"
                    color="error"
                    onClick={onDelete}
                  >
                    {strings.polls.deletePoll}
                  </Button>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {pollStatus === 'FINISHED' ? (
            <Button variant="contained" color="primary" onClick={onClose}>
              {strings.actions.close}
            </Button>
          ) : (
            <>
              <Button color="primary" onClick={onClose}>
                {strings.actions.cancel}
              </Button>
              <LoadingButton
                variant="contained"
                color="primary"
                disabled={!isDirty}
                loading={isSubmitting}
                onClick={onSubmit}
              >
                {strings.actions.save}
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </BaseDialog>
      <PollValidationPopover
        showPopover={showPopover}
        anchorElRef={anchorEl.current}
        onClose={() => setShowPopover(false)}
      />
    </>
  );
};

export default PollFreeModal;
